// src/components/SessionTracker.js
import React, { useEffect, useState } from "react";
import { Clock, Zap, DollarSign, Crown } from "lucide-react";
import "./SessionTracker.css";
import { useAuth } from '../hooks/useAuth';

const SessionTracker = ({ 
  className = ""
}) => {
  const { user } = useAuth();
  const [totalSpentTokens, setTotalSpentTokens] = useState(0);
  const [totalSpentCost, setTotalSpentCost] = useState(0);
  const [sessionTokensUsed, setSessionTokensUsed] = useState(0);
  const [userMembership, setUserMembership] = useState(null);
  const [sessionLimits, setSessionLimits] = useState({ maxTokens: 10000, tokensPerHour: 500 });

  // Load user profile with session data - less frequent
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/user/profile`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSessionTokensUsed(data.sessionTokensUsed || 0);
          setSessionLimits({
            maxTokens: data.maxTokens || 10000,
            tokensPerHour: data.tokensPerHour || 500
          });
          setUserMembership(data.membership);
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };
    
    if (user) {
      loadUserProfile();
      // Refresh session data every 2 minutes instead of 30 seconds
      const interval = setInterval(loadUserProfile, 120000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Calculate real session data
  const actualSessionUsed = Math.min(sessionTokensUsed, sessionLimits.maxTokens);
  const sessionTokensRemaining = Math.max(0, sessionLimits.maxTokens - actualSessionUsed);
  const sessionProgress = (actualSessionUsed / sessionLimits.maxTokens) * 100;
  const isBlocked = actualSessionUsed >= sessionLimits.maxTokens;

  // === Load from localStorage and user data ===
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("sessionStats")) || {};
    if (saved.tokensUsed) setSessionTokensUsed(saved.tokensUsed);
    
    // Use server data for total stats
    if (user?.totalTokensUsed) setTotalSpentTokens(user.totalTokensUsed);
    if (user?.totalCost) setTotalSpentCost(user.totalCost);
  }, [user]);
  
  // Listen for real-time stats updates from server
  useEffect(() => {
    const handleStatsUpdate = (event) => {
      const { totalTokensUsed, totalCost, balance } = event.detail;
      setTotalSpentTokens(totalTokensUsed);
      setTotalSpentCost(totalCost);
      
      // Update global window stats
      window.sessionStats = {
        ...window.sessionStats,
        totalTokens: totalTokensUsed,
        totalCost: totalCost,
        balance: balance
      };
    };
    
    window.addEventListener('userStatsUpdated', handleStatsUpdate);
    return () => window.removeEventListener('userStatsUpdated', handleStatsUpdate);
  }, []);

  // === Save to localStorage + sync global ===
  useEffect(() => {
    const stats = {
      tokensUsed: sessionTokensUsed,
      totalTokens: totalSpentTokens,
      totalCost: totalSpentCost,
      isBlocked,
    };
    localStorage.setItem("sessionStats", JSON.stringify(stats));

    // 👇 Make globally available
    window.sessionStats = stats;
    
    // Emit event for real-time updates
    window.dispatchEvent(new CustomEvent('sessionStatsUpdated', {
      detail: stats
    }));
  }, [sessionTokensUsed, totalSpentTokens, totalSpentCost, isBlocked]);

  // === Global consumeTokens function ===
  window.consumeTokens = (tokens) => {
    // Don't block token consumption - let server handle limits
    setSessionTokensUsed((prev) => {
      const newValue = prev + tokens;
      // Save to localStorage immediately
      localStorage.setItem("sessionStats", JSON.stringify({
        tokensUsed: newValue,
        totalTokens: totalSpentTokens + tokens,
        totalCost: totalSpentCost + (tokens * 0.0001),
        isBlocked: newValue >= sessionLimits.maxTokens,
        balance: window.sessionStats?.balance || 0
      }));
      return newValue;
    });
    setTotalSpentTokens((prev) => prev + tokens);
    setTotalSpentCost((prev) => prev + tokens * 0.0001);
    return true; // Always return true, let server handle validation
  };

  return (
    <div className={`session-tracker ${className}`}>
      <div className="session-header">
        <div className="session-title">
          <Clock size={14} />
          <span>Session Status</span>
        </div>
      </div>

      <div className="session-stats">
        {userMembership && (
          <div className="stat-item">
            <Crown size={12} />
            <div className="stat-details">
              <span className="stat-label">Membership</span>
              <span className="stat-value">{userMembership.name}</span>
              <span className="stat-sub">Expires: {new Date(userMembership.expiration_date).toLocaleDateString()}</span>
            </div>
          </div>
        )}
        
        <div className="stat-item">
          <DollarSign size={12} />
          <div className="stat-details">
            <span className="stat-label">Total Spent</span>
            <span className="stat-value">${totalSpentCost.toFixed(4)}</span>
            <span className="stat-sub">{totalSpentTokens.toLocaleString()} tokens</span>
          </div>
        </div>

        <div className="stat-item">
          <Zap size={12} />
          <div className="stat-details">
            <span className="stat-label">Session Usage</span>
            <span className="stat-value">{sessionTokensRemaining.toLocaleString()} left</span>
            <span className="stat-sub">
              {actualSessionUsed.toLocaleString()} / {sessionLimits.maxTokens.toLocaleString()} used
            </span>
          </div>
        </div>

        <div className="session-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${Math.min(sessionProgress, 100)}%` }}
            />
          </div>
          <div className="progress-info">
            <span className="progress-text">{Math.min(sessionProgress, 100).toFixed(1)}% used</span>
            <span className="refresh-time">+{sessionLimits.tokensPerHour} every hour</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTracker;
